import React, {ReactElement, ReactNode} from "react";
import {
    getMaxWineTextLength,
    MAX_WINE_NAME_LENGTH,
    MenuFormat,
    WineItem
} from "../MenuDocumentModel";
import {FormattedNumber} from "react-intl";
import {ProgressBar} from "components/UIComponents/ProgressBar";
import {Flag} from "components/UIComponents/Flag";
import {Badge} from "components/UIComponents/Badge";
import {
    EditorItem, getMmCssValue,
    getShowTasteSweetness,
    getShowWineBadgesOrFood, getShowWineFood, getWineBadgesHeight, getWineHeightMm,
    getWineItemShowAppellation,
    getWineItemShowYear, getWineMarginBottomMm, isLeafletDocument
} from "../editorUtils";
import {useAppSelector} from "../../../lib/redux/hooks";
import {menuEditorSelectors} from "../editorSlice";
import {isNil} from "ramda";
import { LeafletPreviewItem } from "./LeafletPreviewItem";
import { getTranslations, Language } from "features/i18n/translations";
import { WineText } from "./WineText";

export interface WinePreviewItemProps {
    format: MenuFormat
    wineItem: EditorItem<WineItem>
    degu: boolean
    isPreview?: boolean
}

export function WinePreviewItem(props: WinePreviewItemProps): ReactElement {
    const document = useAppSelector(menuEditorSelectors.document);
    if (document && isLeafletDocument(document)) {
        return <LeafletPreviewItem {...props} />
    }
    return props.format === 'a4-half' ? (
        <A4HalfWinePreviewItem {...props} />
    ) : (
        <RegularWinePreviewItem {...props} />
    );
}

export function joinNodes(nodes: ReactNode[], separator: ReactNode) {
    nodes = nodes.filter(Boolean);
    return nodes.map((el, i) => {
        return <React.Fragment key={i}>
            {el}
            {i!==nodes.length-1&&separator}
        </React.Fragment>
    })
}

function RegularWinePreviewItem(props: WinePreviewItemProps): ReactElement {
    const document = useAppSelector(menuEditorSelectors.document);
    const wine = props.wineItem.menuItem;
    const isPreview = props.isPreview;
    const hasError = isPreview && (wine.name.length > MAX_WINE_NAME_LENGTH || wine.text.length > getMaxWineTextLength(props.format));

    if (!document) return <></>;

    const language = document.language || "de";
    const translations = getTranslations(language);
    
    const appellation = getWineItemShowAppellation(wine, document)?wine.appellation:"";
    const year = getWineItemShowYear(wine, document)?wine.year:"";

    const isA4 = props.format === 'a4',
        isA5 = props.format === 'a5';

    const scale = wine.img_scale ? wine.img_scale : (isA4?1.25:1.10);

    const imgStyle = {
        '--tw-scale-x': scale,
        '--tw-scale-y': scale,
        'height': getMmCssValue(
            (getWineHeightMm(props.format, wine)||0) - getWineBadgesHeight(props.format, wine)
        )
    };

    return (
        <article className={`w-full overflow-hidden
                            flex items-stretch gap-3 relative`}
            style={{
                height: getMmCssValue(getWineHeightMm(props.format, wine)),
                marginBottom: getMmCssValue(getWineMarginBottomMm(props.format), "0")
            }}
        >
          {wine.detailed&&<div className="self-stretch w-[14mm] flex-none overflow-hidden flex justify-center items-center">
              {wine.img_url && (
                <img alt={wine.name}
                     className={`transform ${scale} object-cover object-center`}
                     style={imgStyle as any}
                     src={wine.img_url}
                />
              )}
          </div>}

            <div className={`w-[80mm] py-[.5mm] flex-auto flex flex-col ${hasError && 'bg-[#AF143033] rounded'}`}
                 style={{
                     gap: isA4 ? '0.3rem' : isA5 ? '0.2rem' : 'normal'
                 }}
            >
                <div className="flex flex-none items-baseline gap-1">
                    <h2 className={`font-barlow 
                        ${isA4?'text-[14pt]':isA5?'text-[12pt]':''}
                        font-medium leading-none flex text-${isPreview && wine.name.length > MAX_WINE_NAME_LENGTH ? 'primary' : 'text'}`}>
                        {wine.name}
                    </h2>
                    {appellation&&<span className="font-barlow text-[10pt] text-text leading-none">
                        | {appellation}
                    </span>}
                </div>

                <div className="flex items-baseline gap-2"
                    style={{
                        height: isA4?'10pt':'8pt'
                    }}
                >
                    <div className={`font-barlow text-text leading-none ${isA4?'text-[10pt]':isA5?'text-[8pt]':''} whitespace-nowrap`}>
                        {joinNodes(
                            [
                                year,
                                wine.size&&`${wine.size}cl`,
                                wine.grapes
                            ],
                            <>
                                &nbsp;&nbsp;
                                <span className="text-gray-1">|</span>
                                &nbsp;&nbsp;
                            </>
                        )}
                    </div>

                    <div className="flex-auto pr-2">
                        <hr className="h-px t-2 border-dotted border-t border-text"/>
                    </div>

                    <div className={`flex-none flex gap-1 leading-none
                        ${props.format === 'a4' ? 'text-[10pt]' : ''}
                        ${props.format === 'a5' ? 'text-[8pt]' : ''}
                    `}>
                        <PriceInfo degu={props.degu} wine={wine} cellWidth={props.format === 'a4' ? '3.5rem' : '3rem'} />
                    </div>
                </div>

                <div className="flex flex-col gap-1">
                    <div className="flex items-baseline gap-1 leading-none"
                        style={{
                            height: isA4?'10pt':'8pt'
                        }}
                    >
                        {wine.detailed && <div className="self-center h-full">
                            <Flag country={wine.country}
                                className={`h-full`}
                            />
                        </div> }
                        <span className={`font-barlow text-[${isA4?'10pt':'8pt'}]`}>
                            {joinNodes([
                                wine.country&&<span className={`${wine.detailed?'font-bold':''}`}>{wine.country}</span>,
                                wine.region,
                                wine.producer
                            ], <>,&nbsp;</>)}
                        </span>
                    </div>
                </div>

                {wine.detailed && (
                    <div className="flex">
                        <div className={"grow"}>
                            {wine.showDescription && (
                                <div className={`font-barlow leading-tight overflow-hidden ${isPreview && wine.text.length > getMaxWineTextLength(props.format) ? 'text-primary' : ''}
                                    ${isA4 ? 'text-[8pt] max-w-[95mm]' : ''}
                                    ${isA5 ? 'text-[7pt] max-w-[70mm]' : ''}
                                `} style={{
                                    maxHeight: isA4 ? '29pt' : isA5 ? '27pt' : 'auto',
                                    marginBottom: isA4 ? '0.25rem' : isA5 ? '0.2rem' : ''
                                }}>
                                    <WineText text={wine.text}/>
                                </div>
                            )}
                        </div>
                        {getShowTasteSweetness(wine) && (
                            <div className={`w-[42mm] flex flex-col
                                 ${isA4 ? 'text-[8pt]' : ''}
                                 ${isA5 ? 'text-[7pt]' : ''}`}>
                                <TasteBar taste={wine.taste} language={language}/>
                                <SweetnessBar sweetness={wine.sweetness} language={language}/>
                            </div>
                        )}
                    </div>
                )}
                {getShowWineBadgesOrFood(wine)&&
                    <div className={`font-barlow leading-tight flex items-center gap-2
                        ${isA4 ? 'text-[8pt]' : ''}
                        ${isA5 ? 'text-[7pt]' : ''}
                    `}>
                        {wine.badges?.map((b, index) => <Badge key={index} type={b}/>) ?? null}

                        {getShowWineFood(wine) && (
                            <>{translations["editor.wine.suitableFood"]}: {wine.suitableFood}</>
                        )}
                    </div>
                }
            </div>
        </article>
    );
}


function A4HalfWinePreviewItem(props: WinePreviewItemProps): ReactElement {
    const document = useAppSelector(menuEditorSelectors.document);
    const wine = props.wineItem.menuItem;
    const isPreview = props.isPreview;
    const hasError = isPreview && (wine.name.length > MAX_WINE_NAME_LENGTH || wine.text.length > getMaxWineTextLength(props.format));

    if (!document) return <></>;

    const language = document.language || "de";
    const translations = getTranslations(language);

    const appellation = getWineItemShowAppellation(wine, document)?wine.appellation:"";
    const year = getWineItemShowYear(wine, document)?wine.year:"";

    const scale = wine.img_scale ? `scale-${wine.img_scale*100}` : 'scale-125';

    return (
        <article className={`w-full overflow-hidden
                            flex items-stretch gap-3 relative`}
                 style={{
                     height: getMmCssValue(getWineHeightMm(props.format, wine)),
                     marginBottom: getMmCssValue(getWineMarginBottomMm(props.format), "0")
                 }}
        >
            {wine.detailed&&<div className="self-stretch w-[14mm] flex-none overflow-hidden flex justify-center items-center">
                {wine.img_url &&
                    <img alt={wine.name}
                        className={`transform ${scale} object-cover object-center`}
                        src={wine.img_url}
                        style={{
                            height: getMmCssValue(
                                (getWineHeightMm(props.format, wine)||0) - getWineBadgesHeight(props.format, wine)
                            )
                        }}
                    />
                }
            </div>}

            <div className={`w-[80mm] py-[.5mm] flex-auto flex flex-col gap-0.5 ${hasError && 'bg-[#AF143033] rounded'}`}>
                <div className="flex flex-col" style={{gap: "0.2rem"}}>
                    <div className="flex flex-none items-baseline gap-1">
                        <h2 className={`font-barlow text-[10pt] font-medium leading-none flex text-${isPreview && wine.name.length > MAX_WINE_NAME_LENGTH ? 'primary' : 'text'}`}>
                            {wine.name}
                        </h2>
                        {appellation&&<span className="font-barlow text-[8pt] text-text leading-none">
                            | {appellation}
                        </span>}
                        {year&&<span className="font-barlow text-[8pt] text-text leading-none">
                            | {year}
                        </span>}
                    </div>
                    <div className="flex items-baseline gap-1"
                        style={{height:'8pt'}}
                    >
                        <div className="font-barlow text-[7pt] leading-none text-text whitespace-nowrap">
                            {joinNodes(
                                [
                                    wine.size&&`${wine.size}cl`,
                                    wine.grapes
                                ],
                                <>
                                    &nbsp;&nbsp;
                                    <span className="text-gray-1">|</span>
                                    &nbsp;&nbsp;
                                </>
                            )}
                        </div>
                        <div className="flex-auto">
                            <hr className="h-px t-2 border-dotted border-t border-text"/>
                        </div>
                        <div className={`flex-none flex leading-none ${props.degu?'gap-1':'gap-2'} text-[8pt]`}>
                            <PriceInfo degu={props.degu} wine={wine} cellWidth={"2.2rem"} />
                        </div>
                    </div>
                    <div className="flex items-baseline gap-1"
                        style={{height:'8pt'}}
                    >
                        {wine.detailed && <div className="self-center h-full"><Flag country={wine.country} className="h-full"/></div> }
                        <div className="font-barlow leading-none text-[8pt]">
                            {joinNodes([
                                wine.country&&<span className={`${wine.detailed?'font-bold':''}`}>{wine.country}</span>,
                                wine.region,
                                wine.producer
                            ], <>,&nbsp;</>)}
                        </div>
                    </div>
                    {wine.detailed && (
                        <div className="flex flex-col gap-1 text-[7pt]">
                            {wine.showDescription && (
                                <div className={`font-barlow leading-tight mr-3 max-h-[26pt] overflow-hidden max-w-[80mm] ${isPreview && wine.text.length > getMaxWineTextLength(props.format) ? 'text-primary' : ''}`}>
                                    <WineText text={wine.text}/>
                                </div>
                            )}

                            {wine.detailed && (wine.showTasteSweetness == null || wine.showTasteSweetness) && (
                                <div className="flex gap-4 items-center justify-between">
                                    <TasteBar taste={wine.taste} language={language}/>
                                    <SweetnessBar sweetness={wine.sweetness} language={language}/>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {getShowWineBadgesOrFood(wine)&&
                    <div className={`font-barlow leading-tight flex items-center gap-2 mt-1 text-[7pt]`}>
                        {wine.badges?.map((b, index) => <Badge key={index} type={b}/>) ?? null}

                        {getShowWineFood(wine) && (
                            <>{translations["editor.wine.suitableFood"]}: {wine.suitableFood}</>
                        )}
                    </div>
                }
            </div>

        </article>
    );
}

function PriceInfo({degu, wine, cellWidth}: { degu: boolean, wine: WineItem, cellWidth: string}): ReactElement {
    const prices = wine.displayPrices ?? [];
    return degu ? (
        <>
            <span className={`text-center whitespace-nowrap`} style={{width: cellWidth}}>{wine.objectID}</span>
            <span className="w-12 text-center font-bolda" style={{width: cellWidth}}>
                <FormattedNumber
                    minimumFractionDigits={2}
                    maximumFractionDigits={2}
                    value={wine.purchasePricePerBottle ?? 0}
                />
            </span>
        </>
    ) : (prices.length > 0) ? (
        <Prices prices={prices} cellWidth={cellWidth} />
    ) : (
        <>&nbsp;</> /* this behaves weird in PDF if prices are empty unless we make sure there is at least this space */
    )
}

function Prices({prices, cellWidth}: { prices: WineItem['prices'], cellWidth: string }): ReactElement {
    const filteredPrices: WineItem['prices'] = [];
    let insert = false;
    for (let price of prices) {
        if (insert) {
            filteredPrices.push(price);
        } else if (!isNil(price)) {
            insert = true;
            filteredPrices.push(price);
        }
    }
    if (filteredPrices.length === 0) {
        return <>&nbsp;</>;
    }
    return (
        <>
            {filteredPrices.map((price, index) => (
                <span key={index} className="overflow-hidden text-center" style={{width: cellWidth}}>
                    {price != null ? (
                        <FormattedNumber
                            minimumFractionDigits={2}
                            maximumFractionDigits={2}
                            value={price}
                        />
                    ) : (
                        <>&nbsp;</>
                    )}
                </span>
            ))}
        </>
    )
}

function TasteBar({taste, language}: { taste: number | null | undefined, language: Language }): ReactElement | null {
    if (taste == null) {
        return null;
    }
    const translations = getTranslations(language);

    return (
        <div className="flex items-center gap-2 justify-end pt-px">
            <span className="leading-tight whitespace-nowrap">{translations["editor.wine.taste"]}</span>
            <div className="w-14 h-[3px]"><ProgressBar fillPercent={taste * 10}/></div>
        </div>
    );
}

function SweetnessBar({sweetness, language}: { sweetness: number | null | undefined, language: Language}): ReactElement | null {
    if (sweetness == null) {
        return null;
    }
    const translations = getTranslations(language);

    return (
        <div className="flex items-center gap-2 justify-end pt-px w-full">
            <span className="leading-tight whitespace-nowrap">{translations["editor.wine.sweetness"]}</span>
            <div className="w-14 h-[3px]"><ProgressBar fillPercent={sweetness * 10}/></div>
        </div>
    );
}
